import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import PDPProtectionPlanSelector from 'components/consumer/PDP/PDPProtectionPlanSelector';
import useProductEFGInternalStatus from 'data-hooks/useProductEFGInternalStatus';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import BrowserOnly from 'components/consumer/BrowserOnly';
import ReRenderOnMount from 'components/consumer/ReRenderOnMount';
import useIsBrowser from 'hooks/useIsBrowser';
import PDPNameSection from '../PDPNameSection';
import PDPMobileStudioPhotoGallery from '../PDPMobileStudioPhotoGallery';
import PDPShopTabsBar from '../PDPShopTabsBar';
import PDPAvailableInSizes from '../PDPAvailableInSizes';
import PDPOnDisplayInShowroom from '../PDPOnDisplayInShowroom';
import PDPMobileConfigurationPhotoGallery from '../PDPMobileConfigurationPhotoGallery';
import PDPChooseYourOptions from '../PDPChooseYourOptions';
import PDPUpSellProduct from '../PDPUpSellProduct';
import PDPCompleteOrder from '../PDPCompleteOrder';
import PDPAddToBagSection from '../PDPAddToBagSection';
import PDPKeySellingPoints from '../PDPKeySellingPoints';
import PDPSellingAttributes from '../PDPSellingAttributes';
import PDPProductDescription from '../PDPProductDescription';
import PDPDimensionsAndDetails from '../PDPDimensionsAndDetails';
import BVReviewsOnShowHost from '../BazaarVoice/BVReviewsOnShowHost';
import PDPCustomerPhotos from '../PDPCustomerPhotos';
import PDPBreadCrumbs from '../PDPBreadCrumbs';
import PDPAlertBar from '../PDPAlertBar';
import PDPDetailsAndProductCareLink from '../PDPDetailsAndProductCareLink';
import PDPAlertsSection from '../PDPAlertsSection';
import PDPDiscontinuedMessage from '../PDPDiscontinuedMessage';
import usePDPOptions from '../state/context/usePDPOptions';
import PDPMobileGalleryRevamp from '../PDPMobileGalleryRevamp';
import useIsGalleryRevampVariant from '../useIsGalleryRevampVariant';
const PDPCollectionSlider = loadable(() => import('../PDPCollectionSlider'));
const ModularAddProductsMarxent = loadable(() => import('components/consumer/modular-configurator/ModularAddProducts/marxent'));

/**
 * Mobile layout: gallery shell sits above the stock-gated options rail.
 * Outer full-page Suspense was removed — hero/context boundaries live in
 * PDPHeroShell / PDPContextRegion; sections keep their own.
 */
const PDPMobile = t0 => {
  const $ = _c(76);
  const {
    slug,
    isOutOfStock: t1,
    showOutOfStockNotification: t2,
    pdpIsModularBYOProduct: t3,
    isEditOrder: t4,
    showDiscontinuedNotification: t5,
    discontinuedCategoryMsg: t6,
    productHeroImage: t7,
    productCylindoHeroImage: t8
  } = t0;
  const isOutOfStock = t1 === undefined ? false : t1;
  let t9;
  if ($[0] !== t2) {
    t9 = t2 === undefined ? {} : t2;
    $[0] = t2;
    $[1] = t9;
  } else {
    t9 = $[1];
  }
  const showOutOfStockNotification = t9;
  const pdpIsModularBYOProduct = t3 === undefined ? false : t3;
  const isEditOrder = t4 === undefined ? false : t4;
  const showDiscontinuedNotification = t5 === undefined ? false : t5;
  const discontinuedCategoryMsg = t6 === undefined ? null : t6;
  const productHeroImage = t7 === undefined ? null : t7;
  const productCylindoHeroImage = t8 === undefined ? null : t8;
  const {
    isEFGInternal: t10
  } = useProductEFGInternalStatus();
  const isEFGInternal = t10 === undefined ? false : t10;
  const {
    data: optionData
  } = usePDPOptions();
  const isBrowser = useIsBrowser();
  const isGalleryRevampVariant = useIsGalleryRevampVariant();
  const showGalleryRevamp = isGalleryRevampVariant && !pdpIsModularBYOProduct;
  const [displayPressComp, setDisplayPressComp] = useState(false);
  const [marxentSelectedFabric, setMarxentSelectedFabric] = useState(null);
  const [hideMarxent, setHideMarxent] = useState(true);
  const [unavailableFabric, setUnavailableFabric] = useState(false);
  const galleryRef = useRef(null);
  let t11;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      if (galleryRef.current) {
        galleryRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      }
    };
    $[2] = t11;
  } else {
    t11 = $[2];
  }
  const scrollToGallery = t11;
  let t12;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="mb-4"><PDPBreadCrumbs /></div>;
    $[3] = t12;
  } else {
    t12 = $[3];
  }
  let t13;
  if ($[4] !== isEditOrder) {
    t13 = <PDPNameSection isEditOrder={isEditOrder} />;
    $[4] = isEditOrder;
    $[5] = t13;
  } else {
    t13 = $[5];
  }
  let t14;
  if ($[6] !== isBrowser || $[7] !== isEditOrder) {
    t14 = !isEditOrder && isBrowser ? <PDPAvailableInSizes /> : <div className="PDP__PDPAvailableInSizes-filler" />;
    $[6] = isBrowser;
    $[7] = isEditOrder;
    $[8] = t14;
  } else {
    t14 = $[8];
  }
  let t15;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <PDPOnDisplayInShowroom />;
    $[9] = t15;
  } else {
    t15 = $[9];
  }
  let t16;
  if ($[10] !== t14) {
    t16 = <FixedWidthContainer>{t14}{t15}</FixedWidthContainer>;
    $[10] = t14;
    $[11] = t16;
  } else {
    t16 = $[11];
  }
  let t17;
  if ($[12] !== pdpIsModularBYOProduct || $[13] !== productCylindoHeroImage || $[14] !== productHeroImage || $[15] !== showGalleryRevamp || $[16] !== slug) {
    t17 = <div>{showGalleryRevamp ? <PDPMobileGalleryRevamp slug={slug} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} /> : <PDPMobileStudioPhotoGallery pdpIsModularBYOProduct={pdpIsModularBYOProduct} productHeroImage={productHeroImage} productCylindoHeroImage={productCylindoHeroImage} />}</div>;
    $[12] = pdpIsModularBYOProduct;
    $[13] = productCylindoHeroImage;
    $[14] = productHeroImage;
    $[15] = showGalleryRevamp;
    $[16] = slug;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  let t18;
  if ($[18] !== isEditOrder) {
    t18 = <FixedWidthContainer><PDPShopTabsBar isEditOrder={isEditOrder} /></FixedWidthContainer>;
    $[18] = isEditOrder;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  let t19;
  if ($[20] !== hideMarxent || $[21] !== isEFGInternal || $[22] !== isEditOrder || $[23] !== optionData?.hasSingleOptionValue || $[24] !== pdpIsModularBYOProduct || $[25] !== showGalleryRevamp || $[26] !== slug || $[27] !== unavailableFabric) {
    t19 = <ReRenderOnMount>{!showGalleryRevamp && (!isEFGInternal || isEFGInternal && !optionData?.hasSingleOptionValue) ? <div className="PDPMobile__PDPMobileConfigurationPhotoGallery-wrapper" ref={galleryRef}><PDPMobileConfigurationPhotoGallery slug={slug} isEditOrder={isEditOrder} pdpIsModularBYOProduct={pdpIsModularBYOProduct} marxentPostHogFlag="test" hideMarxent={hideMarxent} unavailableFabric={unavailableFabric} /></div> : <div className="PDPMobile__PDPMobileConfigurationPhotoGallery-wrapper-filler" />}</ReRenderOnMount>;
    $[20] = hideMarxent;
    $[21] = isEFGInternal;
    $[22] = isEditOrder;
    $[23] = optionData?.hasSingleOptionValue;
    $[24] = pdpIsModularBYOProduct;
    $[25] = showGalleryRevamp;
    $[26] = slug;
    $[27] = unavailableFabric;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== discontinuedCategoryMsg || $[30] !== showDiscontinuedNotification) {
    t20 = showDiscontinuedNotification ? <PDPAlertBar type="error"><PDPDiscontinuedMessage discontinuedCategoryMsg={discontinuedCategoryMsg} /></PDPAlertBar> : <div className="PDPMobile__PDPAlertBar-filler" />;
    $[29] = discontinuedCategoryMsg;
    $[30] = showDiscontinuedNotification;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== isEditOrder || $[33] !== isOutOfStock || $[34] !== marxentSelectedFabric || $[35] !== pdpIsModularBYOProduct || $[36] !== showDiscontinuedNotification || $[37] !== slug || $[38] !== unavailableFabric) {
    t21 = !isOutOfStock && !showDiscontinuedNotification ? <div className="PDPMobile__PDPChooseYourOptions-wrapper">{pdpIsModularBYOProduct && <BrowserOnly><div className="border-t border-gray-light7"><SuspenseWithLoader><ModularAddProductsMarxent marxentSelectedFabric={marxentSelectedFabric} unavailableFabric={unavailableFabric} setHideMarxent={setHideMarxent} setUnavailableFabric={setUnavailableFabric} scrollToGallery={scrollToGallery} key={slug} /></SuspenseWithLoader></div></BrowserOnly>}<PDPChooseYourOptions isEditOrder={isEditOrder} setMarxentSelectedFabric={setMarxentSelectedFabric} />{!isEditOrder && <PDPUpSellProduct />}{!isEditOrder && <PDPProtectionPlanSelector />}</div> : <div className="PDPMobile__PDPChooseYourOptions-wrapper-filler" />;
    $[32] = isEditOrder;
    $[33] = isOutOfStock;
    $[34] = marxentSelectedFabric;
    $[35] = pdpIsModularBYOProduct;
    $[36] = showDiscontinuedNotification;
    $[37] = slug;
    $[38] = unavailableFabric;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== isOutOfStock || $[41] !== showDiscontinuedNotification || $[42] !== showOutOfStockNotification) {
    t22 = isOutOfStock && !showDiscontinuedNotification ? <PDPAlertsSection showOutOfStockNotification={showOutOfStockNotification} /> : <div className="PDPMobile__PDPAlertSection-filler" />;
    $[40] = isOutOfStock;
    $[41] = showDiscontinuedNotification;
    $[42] = showOutOfStockNotification;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== isEditOrder || $[45] !== isOutOfStock) {
    t23 = isEditOrder ? <PDPCompleteOrder /> : <PDPAddToBagSection isOutOfStock={isOutOfStock} />;
    $[44] = isEditOrder;
    $[45] = isOutOfStock;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t20 || $[48] !== t21 || $[49] !== t22 || $[50] !== t23) {
    t24 = <div>{t20}{t21}{t22}{t23}</div>;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  let t26;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <PDPKeySellingPoints />;
    t26 = <PDPSellingAttributes />;
    $[52] = t25;
    $[53] = t26;
  } else {
    t25 = $[52];
    t26 = $[53];
  }
  let t27;
  if ($[54] !== isEditOrder) {
    t27 = !isEditOrder && <PDPProductDescription />;
    $[54] = isEditOrder;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== pdpIsModularBYOProduct) {
    t28 = !pdpIsModularBYOProduct ? <PDPDimensionsAndDetails /> : <PDPDetailsAndProductCareLink />;
    $[56] = pdpIsModularBYOProduct;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== isEditOrder || $[59] !== slug) {
    t29 = !isEditOrder ? <PDPCustomerPhotos slug={slug} /> : <BVReviewsOnShowHost />;
    $[58] = isEditOrder;
    $[59] = slug;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== displayPressComp) {
    t30 = !displayPressComp ? <SuspenseWithLoader><PDPCollectionSlider setDisplayPressComp={setDisplayPressComp} /></SuspenseWithLoader> : <div className="PDPMobile__PDPPress-filler" />;
    $[61] = displayPressComp;
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== t24 || $[64] !== t27 || $[65] !== t28 || $[66] !== t29 || $[67] !== t30) {
    t31 = <FixedWidthContainer>{t24}{t25}{t26}{t27}{t28}{t29}{t30}</FixedWidthContainer>;
    $[63] = t24;
    $[64] = t27;
    $[65] = t28;
    $[66] = t29;
    $[67] = t30;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== t13 || $[70] !== t16 || $[71] !== t17 || $[72] !== t18 || $[73] !== t19 || $[74] !== t31) {
    t32 = <div className="PDPMobile lg:hidden" data-xc="PDPMobile">{t12}{t13}{t16}{t17}{t18}{t19}{t31}</div>;
    $[69] = t13;
    $[70] = t16;
    $[71] = t17;
    $[72] = t18;
    $[73] = t19;
    $[74] = t31;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  return t32;
};
PDPMobile.propTypes = {
  discontinuedCategoryMsg: PropTypes.shape({
    category: PropTypes.string,
    link: PropTypes.string
  }),
  pdpIsModularBYOProduct: PropTypes.bool,
  isEditOrder: PropTypes.bool,
  isOutOfStock: PropTypes.bool,
  showDiscontinuedNotification: PropTypes.bool,
  showOutOfStockNotification: PropTypes.shape({
    productId: PropTypes.number,
    show: PropTypes.bool
  }),
  slug: PropTypes.string,
  productHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  })
};
export default PDPMobile;